/**
 * Profile management: picture, PIN, rename, delete.
 *
 * The list shows everyone, but only the profile you are **currently using** is editable.
 * That mirrors the server rule exactly rather than approximating it: the household shares
 * one login, so nothing else distinguishes members, and letting the settings page offer
 * buttons the server will refuse is worse than not offering them.
 *
 * The way out is the account password, entered once at the top of the page. It unlocks
 * management of every profile — including resetting a PIN nobody remembers, which is
 * otherwise unrecoverable now that deleting a locked profile is gated too.
 *
 * The password is held in component state and never stored. It leaves memory when the
 * page unmounts, which is the correct lifetime for something typed to authorise the next
 * few clicks.
 *
 * Pictures are resized in the browser to a square thumbnail and stored inline on the
 * profile. A phone photo is 3–6 MB; the server cap is 96 KB, so uploading the original
 * would simply fail. Resizing here means the user picks any photo and it works, and the
 * server never receives, decodes or stores a full-size image.
 */
import { useRef, useState, type FormEvent } from 'react';
import type { ProfileDto } from '@filmodasi/shared';
import {
  BUILT_IN_AVATARS,
  PROFILE_AVATAR_MAX_BYTES,
  PROFILE_PIN_LENGTH,
} from '@filmodasi/shared';
import { api, ApiRequestError, setProfileGrant } from '../api/client';
import { useApi } from '../api/hooks';
import { useSession } from '../store/session';
import { ErrorBox, Spinner } from '../components/ui';
import { Avatar, builtInAvatarSrc } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { useConfirm } from '../components/Dialog';

const AVATAR_PX = 256;

/**
 * The languages offered, in the order a Turkish household would want them.
 *
 * A short list rather than every ISO code: this is a preference control, and a select with
 * 180 entries is one nobody uses. Anything missing can still arrive from the classifier —
 * the preference only ranks and, when strict, filters.
 */
const LANGUAGES: { code: string; label: string }[] = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'İngilizce' },
  { code: 'de', label: 'Almanca' },
  { code: 'ar', label: 'Arapça' },
  { code: 'fr', label: 'Fransızca' },
  { code: 'es', label: 'İspanyolca' },
  { code: 'ru', label: 'Rusça' },
];

/**
 * Draws the image into a square canvas, cropping to the centre.
 *
 * WebP first because it is roughly half the size of JPEG at the same quality and every
 * browser this app targets encodes it — but `toDataURL` silently falls back to PNG for an
 * unsupported type, and a PNG photo is enormous, so the result is checked rather than
 * assumed.
 */
async function toAvatarDataUri(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_PX;
  canvas.height = AVATAR_PX;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Tarayıcı görsel işlemeyi desteklemiyor');

  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    AVATAR_PX,
    AVATAR_PX,
  );
  bitmap.close();

  for (const quality of [0.82, 0.65, 0.5]) {
    const uri = canvas.toDataURL('image/webp', quality);
    if (uri.startsWith('data:image/webp') && uri.length <= PROFILE_AVATAR_MAX_BYTES) return uri;

    const jpeg = canvas.toDataURL('image/jpeg', quality);
    if (jpeg.length <= PROFILE_AVATAR_MAX_BYTES) return jpeg;
  }
  throw new Error('Görsel çok büyük, daha küçük bir resim seçin');
}

export function ProfileSettingsPage() {
  const { profile: current, chooseProfile } = useSession();
  const { data, error, loading, reload } = useApi<ProfileDto[]>('/api/profiles');

  const [editing, setEditing] = useState<string | null>(null);
  /** Which profile has its preferences panel open. Separate from the PIN panel. */
  const [tuning, setTuning] = useState<string | null>(null);
  /**
   * The account password, once the owner has proved it.
   *
   * `null` means ordinary mode: you can change your own profile and nothing else.
   */
  const [ownerPassword, setOwnerPassword] = useState<string | null>(null);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(true);
    setFailure(null);
    setMessage(null);
    try {
      await fn();
      setMessage(what);
      reload();
    } catch (err) {
      const code = err instanceof ApiRequestError ? err.code : null;
      setFailure(
        code === 'PROFILE_PIN_INVALID'
          ? 'Mevcut PIN yanlış.'
          : code === 'RATE_LIMITED'
            ? 'Çok fazla deneme yapıldı. Biraz bekleyin.'
            : code === 'PROFILE_LOCKED'
              ? 'Bu profil PIN ile kilitli. Hesap şifresiyle yönetici modunu açın.'
              : // No retry offered, and that is the honest answer: the capability is
                // granted only by a script run against the database, so there is nothing
                // the user could type here that would change it.
                code === 'PROFILE_NOT_ADULT_CAPABLE'
                ? 'Bu profil 18+ içerik için yetkili değil. Yetki yalnızca sunucuda verilir.'
                : err instanceof Error
                  ? err.message
                  : 'İşlem başarısız',
      );
      // A refused override is almost always a mistyped password; dropping it puts the
      // page back in a state whose buttons match what the server will accept.
      if (code === 'FORBIDDEN') setOwnerPassword(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Adds the override to a request body when owner mode is on.
   *
   * The profile headers are deliberately *not* suppressed on these calls any more. They
   * are what proves you are acting as your own profile, which is the ordinary path — the
   * old `withoutProfile: true` stripped exactly the evidence the server now asks for.
   */
  const withProof = (body: Record<string, unknown>) =>
    ownerPassword ? { ...body, accountPassword: ownerPassword } : body;

  /** Matches the server rule, so the page never offers a button that will be refused. */
  const canManage = (p: ProfileDto) => ownerPassword !== null || p.id === current?.id;

  async function pickAvatar(profile: ProfileDto, file: File) {
    await run('Profil resmi güncellendi.', async () => {
      const avatar = await toAvatarDataUri(file);
      const updated = await api<ProfileDto>(`/api/profiles/${profile.id}`, {
        method: 'PATCH',
        body: withProof({ avatar }),
      });
      // Keep the header in step when it is the active profile.
      if (current?.id === updated.id) chooseProfile(updated);
    });
  }

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;

  return (
    <main className="settings">
      <h1>Profiller</h1>
      <p className="settings__hint">
        Sadece <strong>kullandığınız profili</strong> değiştirebilirsiniz. Bir başkasının
        profilini düzenlemek, PIN&apos;ini sıfırlamak veya silmek için hesap şifresi gerekir.
      </p>

      {ownerPassword ? (
        <p className="settings__owner">
          Yönetici modu açık — tüm profiller düzenlenebilir.{' '}
          <button type="button" className="link" onClick={() => setOwnerPassword(null)}>
            Kapat
          </button>
        </p>
      ) : (
        <form
          className="settings__ownerForm"
          onSubmit={(e) => {
            e.preventDefault();
            // Not verified here: there is no endpoint that checks a password on its own,
            // and inventing one would be a free oracle. It is proved by the first
            // request that uses it, which reports back if it is wrong.
            if (passwordDraft) setOwnerPassword(passwordDraft);
            setPasswordDraft('');
          }}
        >
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Hesap şifresi"
            value={passwordDraft}
            onChange={(e) => setPasswordDraft(e.target.value)}
          />
          <button type="submit" disabled={!passwordDraft}>
            Yönetici modu
          </button>
        </form>
      )}

      {message && <p className="settings__ok">{message}</p>}
      {failure && <p className="error">{failure}</p>}

      <ul className="settings__profiles">
        {data?.map((p) => (
          <li key={p.id} className="settings__profile">
            <Avatar profile={p} size={72} />

            <div className="settings__profileMain">
              <strong>{p.name}</strong>
              <span className="settings__meta">
                {/* Was `🔒`, the last live emoji on a profile screen. The picker draws the
                    same lock from the sprite; two different padlocks for one fact is what
                    the icon set exists to stop. */}
                {p.hasPin ? (
                  <>
                    <Icon name="lock" size={0.8} /> PIN korumalı
                  </>
                ) : (
                  'PIN yok'
                )}
                {p.avatar ? ' · resimli' : ''}
                {p.id === current?.id ? ' · bu profildesiniz' : ''}
              </span>
              {!canManage(p) && (
                <span className="settings__locked">
                  Değiştirmek için bu profile geçin veya hesap şifresini girin.
                </span>
              )}
            </div>

            <div className="settings__profileActions">
              {/* The whole block is hidden rather than disabled: a row of greyed-out
                  buttons invites clicking, and the answer is always the same. */}
              <input
                ref={(el) => {
                  fileInputs.current[p.id] = el;
                }}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = ''; // so choosing the same file twice still fires
                  if (file) void pickAvatar(p, file);
                }}
              />
              <button
                type="button"
                disabled={busy || !canManage(p)}
                hidden={!canManage(p)}
                onClick={() => fileInputs.current[p.id]?.click()}
              >
                Resim seç
              </button>

              {p.avatar && canManage(p) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run('Profil resmi kaldırıldı.', async () => {
                      await api(`/api/profiles/${p.id}`, {
                        method: 'PATCH',
                        body: withProof({ avatar: null }),
                      });
                    })
                  }
                >
                  Resmi kaldır
                </button>
              )}

              <button
                type="button"
                disabled={busy || !canManage(p)}
                hidden={!canManage(p)}
                onClick={() => setEditing(editing === p.id ? null : p.id)}
              >
                {p.hasPin ? 'PIN değiştir / kaldır' : 'PIN koy'}
              </button>

              <button
                type="button"
                disabled={busy || !canManage(p)}
                hidden={!canManage(p)}
                onClick={() => setTuning(tuning === p.id ? null : p.id)}
              >
                Tercihler
              </button>
            </div>

            {tuning === p.id && (
              <PreferencesForm
                profile={p}
                busy={busy}
                isCurrent={p.id === current?.id}
                ownerMode={ownerPassword !== null}
                onSave={(patch, what) =>
                  run(what, async () => {
                    const updated = await api<ProfileDto>(`/api/profiles/${p.id}`, {
                      method: 'PATCH',
                      body: withProof(patch),
                    });
                    // The session carries the profile's own policy fields, and the server
                    // reads them from the database on every request — but the *client*
                    // decides what to show from this copy, so a stale one would draw the
                    // wrong screen until the next reload.
                    if (current?.id === updated.id) chooseProfile(updated);
                  })
                }
              />
            )}

            {editing === p.id && (
              <PinForm
                profile={p}
                busy={busy}
                // In owner mode the account password is the proof, so demanding the PIN
                // as well would make a forgotten PIN unresettable — the exact hole this
                // override exists to close.
                asOwner={ownerPassword !== null}
                onSet={(pin, currentPin) =>
                  run('PIN kaydedildi.', async () => {
                    await api(`/api/profiles/${p.id}/pin`, {
                      method: 'PUT',
                      body: withProof(currentPin ? { pin, currentPin } : { pin }),
                    });
                    // The old grant no longer matches the new PIN's intent; drop it so the
                    // next entry re-asks rather than sailing through on a stale unlock.
                    setProfileGrant(p.id, null);
                    setEditing(null);
                  })
                }
                onClear={(currentPin) =>
                  run('PIN kaldırıldı.', async () => {
                    await api(`/api/profiles/${p.id}/pin`, {
                      method: 'DELETE',
                      body: withProof({ currentPin }),
                    });
                    setProfileGrant(p.id, null);
                    setEditing(null);
                  })
                }
              />
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}

/**
 * Avatar, kids mode, language and the 18+ switch.
 *
 * Each control saves on its own rather than through one "Kaydet" button. These are
 * independent settings, and batching them means a refusal on one — the 18+ switch is the
 * one that can be refused — would silently discard the others.
 */
function PreferencesForm({
  profile,
  busy,
  onSave,
  isCurrent,
  ownerMode,
}: {
  profile: ProfileDto;
  busy: boolean;
  onSave: (patch: Record<string, unknown>, what: string) => void;
  /** Whether this is the profile in use, which is what the redirect below acts on. */
  isCurrent: boolean;
  /** Whether the account password is already to hand, which decides how loud to be. */
  ownerMode: boolean;
}) {
  /*
   * Defaulted even though the type says it cannot be missing.
   *
   * The type describes the server this build was compiled against, not the one answering
   * the request. Two situations put an older shape in front of this component: the seconds
   * during a deploy when the new bundle is being served by the old API, and a profile
   * rehydrated from `localStorage` that was stored before these fields existed. Reading
   * `.includes` off `undefined` in either case throws during render, and React unmounts the
   * whole tree — the settings page goes white, and so does everything around it.
   *
   * Found by running the app against a stale server, which is exactly the accident this
   * guards against.
   */
  const languages = profile.languages ?? [];

  const toggleLanguage = (code: string) => {
    const next = languages.includes(code)
      ? languages.filter((c) => c !== code)
      : [...languages, code];
    onSave({ languages: next }, 'Dil tercihi kaydedildi.');
  };

  const { confirm: confirmKid, dialog: kidDialog } = useConfirm();

  /**
   * Turning kids mode on, with a warning that it cannot be turned off the same way.
   *
   * The asymmetry is deliberate on the server — only the account password takes a profile
   * out of kids mode, because the person using a child's profile is the child. But that
   * makes this checkbox a one-way door for anyone who does not have the password to hand,
   * and a one-way door has to say so *before* it closes. Doing it on your own profile is
   * worse still: the route guard removes this screen on the next navigation, so the switch
   * that turned it on is no longer anywhere you can reach.
   *
   * Only asked when owner mode is off. With the password already typed the warning is
   * about an obstacle the person is holding the key to, which is noise.
   */
  const toggleKid = async (next: boolean) => {
    if (next && !ownerMode) {
      const ok = await confirmKid({
        title: 'Çocuk profiline çevrilsin mi?',
        body: isCurrent
          ? 'Bunu geri almak için hesap şifresi gerekir ve bu ekran çocuk profilinde açılmaz — kapatmayı başka bir profilden yapmanız gerekecek.'
          : 'Bunu geri almak için hesap şifresi gerekir.',
        confirmLabel: 'Çocuk profiline çevir',
      });
      if (!ok) return;
    }
    onSave(
      { isKid: next },
      next ? 'Çocuk profiline çevrildi.' : 'Çocuk profili kapatıldı.',
    );
  };

  return (
    <div className="prefs">
      <section className="prefs__group">
        <h3>Avatar</h3>
        <div className="prefs__avatars">
          {BUILT_IN_AVATARS.map((id) => (
            <button
              key={id}
              type="button"
              disabled={busy}
              className={`prefs__avatar${profile.avatarId === id && !profile.avatar ? ' is-active' : ''}`}
              // The id is the label a screen reader gets; the pictures are abstract enough
              // that "reel" or "dragon" says more than any phrase about the drawing would.
              aria-label={id}
              aria-pressed={profile.avatarId === id && !profile.avatar}
              onClick={() =>
                // Clearing `avatar` alongside it: an uploaded picture wins in `Avatar`, so
                // picking an illustration while one is set would appear to do nothing.
                onSave({ avatarId: id, avatar: null }, 'Avatar seçildi.')
              }
            >
              <img src={builtInAvatarSrc(id)} alt="" width={44} height={44} />
            </button>
          ))}
        </div>
        {profile.avatarId && (
          <button
            type="button"
            className="link"
            disabled={busy}
            onClick={() => onSave({ avatarId: null }, 'Avatar kaldırıldı.')}
          >
            Avatarı kaldır
          </button>
        )}
      </section>

      <section className="prefs__group">
        <h3>Çocuk profili</h3>
        <label className="toggle">
          <input
            type="checkbox"
            disabled={busy}
            checked={profile.isKid}
            onChange={(e) => void toggleKid(e.target.checked)}
          />
          Yalnızca çocuklara uygun içerik
        </label>
        <p className="prefs__hint">
          Açıkken yetişkin ve sınıflandırılamamış içerik bu profile hiç gösterilmez — arama,
          öneriler ve doğrudan bağlantılar dahil.
        </p>
        {/*
          Stated on the screen as well as in the dialog. The dialog is only shown to
          whoever flips the switch; this line is what the next person to open the page
          reads, and "why can I not turn this off" is otherwise a question with no answer
          visible anywhere.
        */}
        {profile.isKid && (
          <p className="prefs__hint">
            Kapatmak için hesap şifresi gerekir — çocuk modundan çıkışa yalnızca hesap
            sahibi karar verebilir.
          </p>
        )}
      </section>
      {kidDialog}

      {/*
        Two groups, not one, and the split is what the first version got wrong.
        "Which languages do I want to see in the library" and "which track should play"
        are different questions, and interleaving them left the strict-mode toggle sitting
        under the audio dropdown — visibly detached from the chips it modifies, which is
        the only thing it does anything to.
      */}
      <section className="prefs__group">
        <h3>Kütüphane dilleri</h3>
        <div className="prefs__langs">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              disabled={busy}
              className={`chip${languages.includes(lang.code) ? ' is-active' : ''}`}
              aria-pressed={languages.includes(lang.code)}
              onClick={() => toggleLanguage(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            disabled={busy || languages.length === 0}
            checked={profile.strictLanguage}
            onChange={(e) =>
              onSave({ strictLanguage: e.target.checked }, 'Dil kuralı kaydedildi.')
            }
          />
          Sadece bu diller (katı)
        </label>
        <p className="prefs__hint">
          {/* The default is the safe one, and the reason is worth stating rather than
              leaving the user to discover it by finding half their library missing. */}
          Katı mod kapalıyken bu diller öne çıkarılır, diğerleri gizlenmez. Açıkken dili
          belirlenemeyen içerik de gizlenir — sağlayıcı dil bilgisi tahmin edildiği için bu
          beklediğinizden fazlasını saklayabilir.
        </p>
      </section>

      <section className="prefs__group">
        <h3>Oynatma dili</h3>
        <div className="prefs__tracks">
          <label>
            Ses dili
            <select
              disabled={busy}
              value={profile.audioLanguage ?? ''}
              onChange={(e) =>
                onSave({ audioLanguage: e.target.value || null }, 'Ses dili kaydedildi.')
              }
            >
              <option value="">Yayının kendi seçimi</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Altyazı dili
            <select
              disabled={busy}
              value={profile.subtitleLanguage ?? ''}
              onChange={(e) =>
                onSave({ subtitleLanguage: e.target.value || null }, 'Altyazı dili kaydedildi.')
              }
            >
              <option value="">Kapalı</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="prefs__hint">
          {/* Stated rather than left to be discovered: the setting genuinely does nothing
              on some streams, and a preference that silently fails looks like a bug. */}
          Yayında o dilde bir ses ya da altyazı parçası varsa otomatik seçilir. Tek parçalı
          yayınlarda ve tarayıcının kendi oynatıcısını kullandığı bazı dosyalarda bu ayar
          etkisizdir.
        </p>
      </section>

      {/*
        Drawn only for a capable profile.

        The alternative — always showing it and letting the server refuse — would put a
        switch in front of every household member that fails for all but one of them, with
        no explanation they could act on. `adultCapable` is reported by the API precisely so
        this decision can be made here.
      */}
      {profile.adultCapable && (
        <section className="prefs__group">
          <h3>18+ içerik</h3>
          <label className="toggle">
            <input
              type="checkbox"
              disabled={busy || profile.isKid}
              checked={profile.adultEnabled}
              onChange={(e) =>
                onSave(
                  { adultEnabled: e.target.checked },
                  e.target.checked ? '18+ içerik açıldı.' : '18+ içerik kapatıldı.',
                )
              }
            />
            Yetişkin içeriği göster
          </label>
          {profile.isKid && (
            <p className="prefs__hint">Çocuk profilinde açılamaz.</p>
          )}
        </section>
      )}
    </div>
  );
}

function PinForm({
  profile,
  busy,
  asOwner,
  onSet,
  onClear,
}: {
  profile: ProfileDto;
  busy: boolean;
  /** The account password is already carried, so the current PIN is not needed. */
  asOwner: boolean;
  onSet: (pin: string, currentPin?: string) => void;
  onClear: (currentPin: string) => void;
}) {
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const digits = (v: string) => v.replace(/\D/g, '').slice(0, PROFILE_PIN_LENGTH);
  const complete = pin.length === PROFILE_PIN_LENGTH;
  const needsCurrent = profile.hasPin && !asOwner;
  const currentOk = !needsCurrent || currentPin.length === PROFILE_PIN_LENGTH;

  function submit(e: FormEvent) {
    e.preventDefault();
    onSet(pin, needsCurrent ? currentPin : undefined);
  }

  return (
    <form className="settings__pin" onSubmit={submit}>
      {needsCurrent && (
        <label>
          Mevcut PIN
          <input
            type="password"
            inputMode="numeric"
            value={currentPin}
            onChange={(e) => setCurrentPin(digits(e.target.value))}
          />
        </label>
      )}
      <label>
        Yeni PIN
        <input
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(digits(e.target.value))}
        />
      </label>
      <button type="submit" disabled={busy || !complete || !currentOk}>
        Kaydet
      </button>
      {profile.hasPin && (
        <button
          type="button"
          disabled={busy || !currentOk}
          onClick={() => onClear(currentPin)}
        >
          PIN&apos;i kaldır
        </button>
      )}
    </form>
  );
}
